import { AsyncPipe } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    inject,
    Input,
    OnInit,
    QueryList,
    signal,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, Observable, tap } from 'rxjs';

import { PrivaButtonImportance } from '@priva/components/button';
import { PrivaDialogButton, PrivaDialogComponent, PrivaDialogType } from '@priva/components/dialog-simple';
import { PrivaFormGroupComponent } from '@priva/components/form-group';
import { PrivaInputComponent } from '@priva/components/input';
import { PrivaLoaderComponent } from '@priva/components/loader';
import { PrivaSpinnerComponent } from '@priva/components/spinner';
import { PrivaTableColumnDirective, PrivaTableComponent } from '@priva/components/table';
import { PrivaTitleComponent, PrivaTitleSize } from '@priva/components/title';
import { PrivaValidationComponent, PrivaValidationItemComponent } from '@priva/components/validation';

import { KpiNotificationSettingsFacade } from 'app/kpi-notification-settings/kpi-notification-settings.facade';
import {
    KpiLimit,
    KpiNotificationSettings,
    KpiSetLimit,
} from 'app/kpi-notification-settings/kpi-notification-settings.model';
import { getValidationItems } from 'app/shared/helpers';
import { AppFacade } from 'app/state/app.facade';

import { KpiNotificationsTableColumns } from './constants/columns.constant';
import { GetRowsPipe } from './get-rows.pipe';
import { GetTitlePipe } from './get-title.pipe';

@Component({
    selector: 'analytics-kpi-notifications-dialog',
    templateUrl: './kpi-notifications-dialog.component.html',
    styleUrls: ['./kpi-notifications-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PrivaDialogComponent,
        PrivaTitleComponent,
        PrivaTableComponent,
        PrivaTableColumnDirective,
        PrivaFormGroupComponent,
        ReactiveFormsModule,
        PrivaInputComponent,
        PrivaValidationComponent,
        PrivaValidationItemComponent,
        PrivaLoaderComponent,
        PrivaSpinnerComponent,
        AsyncPipe,
        TranslatePipe,
        GetTitlePipe,
        GetRowsPipe,
    ],
})
export class KpiNotificationDialogComponent implements OnInit, AfterViewInit {
    @ViewChildren('kpiNotificationSet') kpiNotificationSetTables: QueryList<PrivaTableComponent<KpiLimit>>;
    @Input() reportId: string;

    isLoaded$: Observable<boolean> = this.kpiNotificationSettingsFacade.isLoaded$;
    kpiNotificationSettings$: Observable<KpiNotificationSettings>;
    kpiSetLimits: KpiSetLimit[] = [];
    ungroupedLimits: KpiLimit[] = [];

    readonly saveButton: PrivaDialogButton = {
        id: 'save-report',
        importance: PrivaButtonImportance.Primary,
        text: 'GLOBAL.ACTION.SAVE',
    };

    readonly cancelButton: PrivaDialogButton = {
        id: 'cancel',
        importance: PrivaButtonImportance.Secondary,
        text: 'GLOBAL.ACTION.CANCEL',
    };

    readonly closeButton: PrivaDialogButton = {
        id: 'close',
        importance: PrivaButtonImportance.Primary,
        text: 'GLOBAL.ACTION.CLOSE',
    };

    PrivaTitleSize = PrivaTitleSize;
    DialogType = PrivaDialogType;
    buttons = signal<PrivaDialogButton[]>([]);
    columnData = KpiNotificationsTableColumns;
    kpiNotificationForm: FormGroup[] = [];
    unGroupedKpiNotificationForm: FormGroup[] = [];
    showValidationMessages = false;
    test = false;

    getItems = getValidationItems;

    FormControlValidationData = [
        {
            id: 'kpi-lower-limit-max',
            items: [
                {
                    message: 'REPORT_EDITION.DEFINITION.FIELD.MAXLENGTH_VALIDATION',
                    iconClass: 'fas fa-exclamation-circle',
                },
            ],
        },
    ];

    private kpiNotificationSettings: KpiNotificationSettings;
    private readonly validators = [Validators.min(0), Validators.max(10)];
    private readonly destroy: DestroyRef = inject(DestroyRef);

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly appFacade: AppFacade,
        private readonly kpiNotificationSettingsFacade: KpiNotificationSettingsFacade,
    ) {}

    ngOnInit(): void {
        this.initKpiNotificationSettings();
    }

    ngAfterViewInit(): void {
        this.isLoaded$ = this.isLoaded$.pipe(
            filter((isLoaded) => isLoaded),
            tap(() => {
                if (this.kpiNotificationSetTables.length > 0) {
                    this.setupInitialSelectedRows();
                } else {
                    this.kpiNotificationSetTables.changes
                        .pipe(takeUntilDestroyed(this.destroy))
                        .subscribe(() => this.setupInitialSelectedRows());
                }
            }),
        );
    }

    onDialogButtonClick(button: PrivaDialogButton): void {
        switch (button.id) {
            case this.saveButton.id:
                this.showValidationMessages = true;
                if (this.validForm) {
                    const updatedSettings = this.extractFormData();
                    this.kpiNotificationSettingsFacade.saveKpiNotificationSettings(
                        this.reportId,
                        updatedSettings,
                    );
                }
                break;
            case this.cancelButton.id:
            case this.closeButton.id:
                this.close();
                break;
        }
    }

    close(): void {
        this.appFacade.closeDialog();
    }

    get hasValidKpiLimits(): boolean {
        return (
            this.kpiNotificationSettings?.kpiSetLimits?.length > 0 ||
            this.kpiNotificationSettings?.ungroupedLimits?.length > 0
        );
    }

    toggleSelectedItems(selectedKpis: KpiLimit[], index: number): void {
        const formGroup = this.kpiNotificationForm[index];
        const kpiNotificationSetTable = this.kpiNotificationSetTables.toArray()[index];
        const selectedRowIds = new Set(kpiNotificationSetTable.selectedRows().map((row) => row.id));

        // Determine whether to update kpiSetLimits or ungroupedLimits
        const limitsToUpdate =
            index < this.kpiSetLimits.length ? this.kpiSetLimits[index].limits : this.ungroupedLimits;

        // Update isActive state and form control validity
        limitsToUpdate.forEach((limit) => {
            const isSelected = selectedRowIds.has(limit.id);
            limit.isActive = isSelected;
            const control = formGroup.get(limit.id);
            if (control) {
                this.updateFormControlValidity(control, isSelected);
            }
        });

        if (selectedKpis.length === 0) {
            this.removeAllValidators(index);
        }
    }

    showValidation(index: number, kpiId: string): boolean {
        return this.showValidationMessages && this.kpiNotificationForm[index].get(kpiId)?.invalid;
    }

    private initKpiNotificationSettings(): void {
        this.kpiNotificationSettings$ = this.kpiNotificationSettingsFacade.kpiNotificationSettings$.pipe(
            tap((kpiNotificationSettings: KpiNotificationSettings) => {
                this.kpiNotificationSettings = kpiNotificationSettings;

                if (this.hasValidKpiLimits) {
                    this.kpiSetLimits = this.kpiNotificationSettings?.kpiSetLimits;
                    this.ungroupedLimits = this.kpiNotificationSettings?.ungroupedLimits;
                    this.createKpiNotificationForm();
                    this.buttons.set([this.saveButton, this.cancelButton]);
                } else {
                    this.buttons.set([]);
                }
            }),
        );
    }

    private setupInitialSelectedRows(): void {
        this.kpiNotificationSetTables.forEach((table, index) => {
            const selectedRows = this.determineSelectedRows(index).map((kpi) => kpi.id);
            table.setSelection(selectedRows);
            this.applyValidatorsToSelectedRows(table.selectedRows(), index);
        });
    }

    private determineSelectedRows(index: number): KpiLimit[] {
        const kpiSetLimits =
            index < this.kpiSetLimits.length ? this.kpiSetLimits[index].limits : this.ungroupedLimits;
        return kpiSetLimits.filter((limit) => limit.isActive);
    }

    private applyValidatorsToSelectedRows(selectedRows: KpiLimit[], index: number): void {
        selectedRows.forEach((row) => this.addRequiredValidatorToControl(row.id, index));
        this.test = true;
    }

    private addRequiredValidatorToControl(controlName: string, index: number): void {
        const control = this.kpiNotificationForm[index].get(controlName);
        if (control && !control.hasValidator(Validators.required)) {
            this.addRequiredValidator(control);
        }
    }

    createKpiNotificationForm(): void {
        this.kpiNotificationForm = [...this.kpiSetLimits, { limits: this.ungroupedLimits }].map(
            (kpiSetLimit) => this.createFormGroupForLimits(kpiSetLimit.limits),
        );
    }

    private createFormGroupForLimits(limits: KpiLimit[]): FormGroup {
        return limits.reduce((formGroup, limit) => {
            const validators = limit.isActive ? [Validators.required, ...this.validators] : this.validators;
            formGroup.addControl(limit.id, this.formBuilder.control(limit.lowerLimit, validators));
            return formGroup;
        }, this.formBuilder.group({}));
    }

    extractFormData(): KpiNotificationSettings {
        const updatedSettings: KpiNotificationSettings = {
            ...this.kpiNotificationSettings,
            kpiSetLimits: this.kpiSetLimits.map((kpiSetLimit, index) => ({
                ...kpiSetLimit,
                limits: kpiSetLimit.limits.map((limit) => ({
                    ...limit,
                    lowerLimit: this.kpiNotificationForm[index].get(limit.id).value || 0,
                })),
            })),
            ungroupedLimits: this.ungroupedLimits.map((limit) => ({
                ...limit,
                lowerLimit: this.kpiNotificationForm[this.kpiSetLimits.length].get(limit.id).value || 0,
            })),
        };

        return updatedSettings;
    }

    private updateFormControlValidity(control: AbstractControl, isActive: boolean): void {
        if (isActive) {
            this.addRequiredValidator(control);
            return;
        }
        this.resetFormControl(control);
    }

    private addRequiredValidator(control: AbstractControl): void {
        const currentValidators = control.validator
            ? [control.validator, Validators.required]
            : Validators.required;
        control.setValidators(currentValidators);
        control.updateValueAndValidity();
    }

    private removeAllValidators(index: number): void {
        this.showValidationMessages = false;
        const formGroup = this.kpiNotificationForm[index];
        Object.keys(formGroup.controls).forEach((controlName) => {
            const control = formGroup.get(controlName);
            if (control) {
                this.resetFormControl(control);
            }
        });
    }

    private resetFormControl(control: AbstractControl): void {
        control.setErrors(null);
        control.setValidators(this.validators);
        control.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    get validForm(): boolean {
        return this.kpiNotificationForm.every((formGroup) =>
            Object.values(formGroup.controls).every((control) => control.valid),
        );
    }
}
